import { Box, Button, Chip, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";
import React, { useState } from "react";
import theme from "../../assets/theme/theme";
import { useAuth } from "../../contexts/AuthContext";
import { logEvent } from "firebase/analytics";
import { postData } from "../../utils/fetchUtils";
import { analytics } from "../../firebase";
import constants from "../../constants";
import { LoadingButton } from "@mui/lab";
import { usePaymentContext } from "../../contexts/PaymentContext";

type SubscriptionPlanType = "MONTHLY_PREMIUM_PLAN" | "MONTHLY_BASIC_PLAN" | "YEARLY_PREMIUM_PLAN" | "YEARLY_BASIC_PLAN" | "NONE";

const UpgradePlan = () => {
  const { currentSubscriptionStatus, pricingPlans } = usePaymentContext();
  const currentTier = currentSubscriptionStatus?.plan_type === "FREE" ? "FREE" : `${currentSubscriptionStatus?.plan_duration}_${currentSubscriptionStatus?.plan_type}`;
  const [chosenSub, setChosenSub] = React.useState<string | null>(
    currentTier === "FREE" ? "FREE" : `${currentSubscriptionStatus?.plan_duration}_${currentSubscriptionStatus?.plan_type}`
  );
  const [isLoading, setIsLoading] = useState(false);
  const { currentUser } = useAuth();
  const [subscriptionFrequency, setSubscriptionFrequency] = useState<"MONTHLY" | "YEARLY">("MONTHLY");

  const onClickEnrollNow = React.useCallback(
    async (plan: SubscriptionPlanType, data: {
      uid: string | undefined;
      priceLookupKey: string;
    }) => {
      try {
        logEvent(analytics, "begin_checkout", { plan });
        setIsLoading(true);
        const url = `${constants.middleHost}/stripe/create-checkout-session`;
        const token = await currentUser?.getIdToken();
        const headers = {
          Authorization: `Bearer ${token}`,
        };
        const res = await postData(url, data, headers);
        setIsLoading(false);
        window.open(res.data.url, "_blank");
      } catch (error: any) {
        console.error(error);
        setIsLoading(false);
        logEvent(analytics, "error_checkout", {
          plan,
          status: "error",
          message: error.message,
        });
      }
    },
    [currentUser]
  );

  const handleSubscriptionFrequency = (
    _: React.MouseEvent<HTMLElement>,
    newSubscriptionFrequency: "MONTHLY" | "YEARLY"
  ) => {
    if (newSubscriptionFrequency !== null) {
      setSubscriptionFrequency(newSubscriptionFrequency);
      setChosenSub(currentTier);
    }
  };

  return (
    <Box>
      <Button
        variant="outlined"
        color="primary"
        onClick={() => window.history.back()}
        sx={{ ml: 6, mt: 2, borderRadius: 2 }}
        >
        Back
      </Button>
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: 3,
          my: 5,
        }}
      >
        <Typography component={"h1"} sx={{ fontSize: 24, fontWeight: 600 }}>
          {currentTier !== "PREMIUM" ? "Upgrade Plan" : "Modify Subscription Plan"}
        </Typography>
        <ToggleButtonGroup
          color="primary"
          value={subscriptionFrequency}
          exclusive
          onChange={handleSubscriptionFrequency}
          aria-label="subscription frequency"
        >
          <ToggleButton value="MONTHLY" aria-label="monthly" style={{ textTransform: 'none', borderTopLeftRadius: 10, borderBottomLeftRadius: 10}}>
            Pay monthly
          </ToggleButton>
          <ToggleButton value="YEARLY" aria-label="yearly" style={{ textTransform: 'none', borderTopRightRadius: 10, borderBottomRightRadius: 10 }}>
            Pay yearly <Chip label="Save 20%" color="primary" size="small" sx={{ ml: 1 }} /> 
          </ToggleButton>
        </ToggleButtonGroup>
        {subscriptionFrequency === "MONTHLY" ? (
          <Box sx={{ width: "50%" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                border: chosenSub === "MONTHLY_BASIC" ? 2 : 1,
                borderRadius: 2,
                width: "100%",
                borderColor:
                  chosenSub === "MONTHLY_BASIC"
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                cursor: "pointer",
                mb: 2,
              }}
              onClick={() => setChosenSub("MONTHLY_BASIC")}
            >
              <Box sx={{ width: "90%", p: 3 }}>
                <Typography fontSize={18} fontWeight={700}>
                  {pricingPlans?.BASIC_MONTHLY.plan} Plan
                </Typography>
                <Typography fontSize={16}>
                  {pricingPlans?.BASIC_MONTHLY.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  width: "10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={16}>
                  <b>{currentTier === "MONTHLY_BASIC" ? "ACTIVE" : `${pricingPlans?.BASIC_MONTHLY.currency}${pricingPlans?.BASIC_MONTHLY.price}/month`}</b>
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                border: chosenSub === "MONTHLY_PREMIUM" ? 2 : 1,
                borderRadius: 2,
                width: "100%",
                borderColor:
                  chosenSub === "MONTHLY_PREMIUM"
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                cursor: "pointer",
              }}
              onClick={() => setChosenSub("MONTHLY_PREMIUM")}
            >
              <Box sx={{ width: "90%", p: 3 }}>
                <Typography fontSize={18} fontWeight={700}>
                  {pricingPlans?.PREMIUM_MONTHLY.plan} Plan
                </Typography>
                <Typography fontSize={16}>
                  {pricingPlans?.PREMIUM_MONTHLY.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  width: "10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={16}>
                  <b>{currentTier === "MONTHLY_PREMIUM" ? "ACTIVE" : `${pricingPlans?.PREMIUM_MONTHLY.currency}${pricingPlans?.PREMIUM_MONTHLY.price}/month`}</b>
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="primary"
                sx={{
                  width: "100%",
                  mt: 3,
                  ":disabled": {
                    backgroundColor: theme.palette.grey[300],
                    color: theme.palette.text.disabled,
                  },
                }}
                disabled={chosenSub === currentTier}
                loading={isLoading}
                onClick={() => {
                  // handle upgrade logic here
                  onClickEnrollNow(chosenSub === "MONTHLY_BASIC" ? "MONTHLY_BASIC_PLAN" : "MONTHLY_PREMIUM_PLAN", {
                    uid: currentUser?.uid,
                    priceLookupKey: chosenSub === "MONTHLY_BASIC" ? pricingPlans!.BASIC_MONTHLY.lookUpKey : pricingPlans!.PREMIUM_MONTHLY.lookUpKey,
                  })
                }}
              >
                {currentTier !== "MONTHLY_PREMIUM" ? "Upgrade Plan" : "Modify Plan"}
              </LoadingButton>
            </Box>
          </Box>
        ) : (
          <Box sx={{ width: "50%" }}>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                border: chosenSub === "YEARLY_BASIC" ? 2 : 1,
                borderRadius: 2,
                width: "100%",
                borderColor:
                  chosenSub === "YEARLY_BASIC"
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                cursor: "pointer",
                mb: 2,
              }}
              onClick={() => setChosenSub("YEARLY_BASIC")}
            >
              <Box sx={{ width: "90%", p: 3 }}>
                <Typography fontSize={18} fontWeight={700}>
                  {pricingPlans?.BASIC_YEARLY.plan} Plan
                </Typography>
                <Typography fontSize={16}>
                  {pricingPlans?.BASIC_YEARLY.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  width: "10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={16}>
                  <b>{currentTier === "YEARLY_BASIC" ? "ACTIVE" : `${pricingPlans?.BASIC_YEARLY.currency}${pricingPlans?.BASIC_YEARLY.price}/year`}</b>
                </Typography>
              </Box>
            </Box>
            <Box
              sx={{
                display: "flex",
                gap: 2,
                border: chosenSub === "YEARLY_PREMIUM" ? 2 : 1,
                borderRadius: 2,
                width: "100%",
                borderColor:
                  chosenSub === "YEARLY_PREMIUM"
                    ? theme.palette.primary.main
                    : theme.palette.grey[300],
                cursor: "pointer",
              }}
              onClick={() => setChosenSub("YEARLY_PREMIUM")}
            >
              <Box sx={{ width: "90%", p: 3 }}>
                <Typography fontSize={18} fontWeight={700}>
                  {pricingPlans?.PREMIUM_YEARLY.plan} Plan
                </Typography>
                <Typography fontSize={16}>
                  {pricingPlans?.PREMIUM_YEARLY.description}
                </Typography>
              </Box>
              <Box
                sx={{
                  p: 3,
                  width: "10%",
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                }}
              >
                <Typography fontSize={16}>
                  <b>{currentTier === "YEARLY_PREMIUM" ? "ACTIVE" : `${pricingPlans?.PREMIUM_YEARLY.currency}${pricingPlans?.PREMIUM_YEARLY.price}/year`}</b>
                </Typography>
              </Box>
            </Box>
            <Box sx={{ display: "flex", justifyContent: "flex-end" }}>
              <LoadingButton
                variant="contained"
                color="primary"
                sx={{
                  width: "100%",
                  mt: 3,
                  ":disabled": {
                    backgroundColor: theme.palette.grey[300],
                    color: theme.palette.text.disabled,
                  },
                }}
                disabled={chosenSub === currentTier}
                loading={isLoading}
                onClick={() => {
                  // handle upgrade logic here
                  onClickEnrollNow(chosenSub === "YEARLY_BASIC" ? "YEARLY_BASIC_PLAN" : "YEARLY_PREMIUM_PLAN", {
                    uid: currentUser?.uid,
                    priceLookupKey: chosenSub === "YEARLY_BASIC" ? pricingPlans!.BASIC_YEARLY.lookUpKey : pricingPlans!.PREMIUM_YEARLY.lookUpKey,
                  })
                }}
              >
                {currentTier !== "YEARLY_PREMIUM" ? "Upgrade Plan" : "Modify Plan"}
              </LoadingButton>
            </Box>
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default UpgradePlan;
